// frontend/admin-portal/src/components/procurement/SendRfqModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Alert, Input, Checkbox } from "@fiberhome/shared-ui";
import { supplierService } from "../../services/supplierService";
import { requisitionService } from "../../services/requisitionService";

export default function SendRfqModal({ open, requisition, onClose, onSent }) {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [dueAt, setDueAt] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError("");
    setResult(null);
    setSelectedIds([]);
    setDueAt("");
    setMessage("");
    supplierService
      .list({ isActive: true, limit: 100 })
      .then(({ items }) => setSuppliers(items))
      .catch(() => setSuppliers([]));
  }, [open]);

  const toggle = (id, checked) => {
    setSelectedIds((s) =>
      checked ? [...new Set([...s, id])] : s.filter((x) => x !== id),
    );
  };

  const handleSend = async () => {
    if (selectedIds.length === 0) {
      setError("Pick at least one supplier");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await requisitionService.send(requisition.id, {
        supplierIds: selectedIds,
        dueAt: dueAt || null,
        message: message || null,
      });
      setResult(res);
      onSent?.(res);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onClose}
      title={result ? "RFQs Sent" : `Send RFQ to suppliers`}
      size="lg"
      footer={
        result ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSend}
              loading={busy}
              disabled={selectedIds.length === 0}
            >
              Send to {selectedIds.length} supplier
              {selectedIds.length === 1 ? "" : "s"}
            </Button>
          </>
        )
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {!result && (
        <>
          <Alert variant="info" className="mb-3">
            Requisition <strong>{requisition?.code}</strong> —{" "}
            {requisition?.items?.length || 0} items will be included in each
            RFQ.
          </Alert>

          <Input
            label="Response Due By"
            type="date"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
          />
          <Input
            label="Message to suppliers (optional)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="e.g. Please quote FOB, warranty details, and MOQ."
          />

          <h6 className="mt-3 mb-2">Select suppliers</h6>
          <div className="fh-supplier-pick">
            {suppliers.length === 0 && (
              <div className="text-muted p-3">Loading suppliers…</div>
            )}
            {suppliers.map((s) => {
              const checked = selectedIds.includes(s.id);
              return (
                <label
                  key={s.id}
                  className={`fh-supplier-pick__item ${checked ? "is-on" : ""}`}
                >
                  <Checkbox
                    checked={checked}
                    onChange={(c) => toggle(s.id, c)}
                  />
                  <div>
                    <div style={{ fontWeight: 600 }}>{s.name}</div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {s.code} {s.city ? `· ${s.city}` : ""}{" "}
                      {s.country ? `· ${s.country}` : ""}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </>
      )}

      {result && (
        <div>
          <Alert variant="success" className="mb-3">
            Created <strong>{result.created}</strong> RFQ
            {result.created === 1 ? "" : "s"}, skipped{" "}
            <strong>{result.skipped}</strong>.
          </Alert>
          <div className="fh-rfq-sent-list">
            {result.rfqs.map((r) => (
              <div key={r.rfqId} className="fh-rfq-sent-item">
                <span>{r.code}</span>
                {r.skipped && (
                  <span className="text-muted">(already existed)</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}
